import React, { useState, useEffect, useRef } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Button from '../button/Button';
import Toaster from '../common/Toaster';
import Api from '../../helper/Api';
import { connect } from 'react-redux';
import './EventAdvertisementManager.css';

const EventAdvertisementManager = (parentProps) => {
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [eventAdvertisements, setEventAdvertisements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const toaster = useRef();

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      setLoading(true);
      let allEvents = [];
      let currentPage = 1;
      let hasMoreData = true;

      // Loop through all pages to get all event data
      while (hasMoreData) {
        const params = {
          sort_by: 'createdAt',
          order: 'DESC',
          search_event: '',
          search_user: '',
          page: currentPage.toString(),
        };

        const res = await Api('POST', 'cms/event/list', params);
        
        if (res.status === 200) {
          const pageData = res.data.data.rows || [];
          allEvents = [...allEvents, ...pageData];

          // Check if there's more data available
          const totalCount = res.data.data.count || 0;
          const loadedCount = allEvents.length;
          
          if (loadedCount >= totalCount || pageData.length === 0) {
            hasMoreData = false;
          } else {
            currentPage++;
          }
        } else {
          hasMoreData = false;
        }
      }

      // Ensure allEvents is an array before setting state
      const safeEvents = Array.isArray(allEvents) ? allEvents : [];
      setEvents(safeEvents);
    } catch (error) {
      console.error('Error loading events:', error);
      if (toaster.current) {
        toaster.current.error('Failed to load events');
      }
    } finally {
      setLoading(false);
    }
  };


  const loadEventAdvertisements = async (eventId) => {
    try {
      setLoading(true);
      const response = await Api('GET', `cms/advertisement/by-event/${eventId}`);
      if (response.status === 200 && response.data && response.data.data && response.data.data.advertisements) {
        const sortedAds = response.data.data.advertisements.sort((a, b) => 
          (a.displayOrder || 0) - (b.displayOrder || 0)
        );
        setEventAdvertisements(sortedAds);
      } else {
        setEventAdvertisements([]);
      }
    } catch (error) {
      console.error('Error loading event advertisements:', error);
      setEventAdvertisements([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEventChange = (eventId) => {
    setSelectedEventId(eventId);
    if (eventId) {
      loadEventAdvertisements(eventId);
    } else {
      setEventAdvertisements([]);
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(eventAdvertisements);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updatedItems = items.map((item, index) => ({
      ...item,
      displayOrder: index + 1
    }));

    setEventAdvertisements(updatedItems);
  };


  const removeAdvertisementFromEvent = async (eventAdvertisementId) => {
    if (!eventAdvertisementId) return;

    if (!window.confirm('Are you sure you want to remove this advertisement from the event?')) {
      return;
    }

    try {
      const response = await Api('DELETE', `cms/advertisement/event-association/${eventAdvertisementId}`);
      
      if (response.status === 200) {
        if (toaster.current) {
          toaster.current.success('Advertisement removed successfully');
        }
        loadEventAdvertisements(selectedEventId);
      }
    } catch (error) {
      console.error('Error removing advertisement:', error);
      if (toaster.current) {
        toaster.current.error('Failed to remove advertisement');
      }
    }
  };

  const saveDisplayOrder = async () => {
    if (!selectedEventId || eventAdvertisements.length === 0) return;

    try {
      setSaving(true);
      const orderData = eventAdvertisements.map((ad, index) => ({
        eventAdvertisementId: ad.eventAdvertisementId,
        displayOrder: index + 1
      }));

      const response = await Api('PUT', 'cms/advertisement/update-event-order', {
        eventId: parseInt(selectedEventId),
        orderData
      });

      if (response.status === 200) {
        if (toaster.current) {
          toaster.current.success('Display order saved successfully');
        }
      }
    } catch (error) {
      console.error('Error saving display order:', error);
      if (toaster.current) {
        toaster.current.error('Failed to save display order');
      }
    } finally {
      setSaving(false);
    }
  };


  const selectedEvent = events.find(event => event.id === parseInt(selectedEventId));

  return (
    <div className="event-advertisement-manager">
      <div className="manager-header">
        <h3>
          <i className="fas fa-sort mr-2"></i>
          Advertisement Display Order Management
        </h3>
        <p className="text-muted">
          Manage the order in which advertisements are displayed for each event
        </p>
      </div>

      <div className="row">
        <div className="col-md-4">
          <div className="event-selector">
            <label htmlFor="eventSelect" className="form-label">
              <strong>Select Event:</strong>
            </label>
            <select
              id="eventSelect"
              className="form-control"
              value={selectedEventId}
              onChange={(e) => handleEventChange(e.target.value)}
            >
              <option value="">-- Choose an Event --</option>
              {events.map(event => (
                <option key={event.id} value={event.id}>
                  {event.eventName}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="col-md-8">
          {selectedEventId && (
            <div className="event-advertisements">
              <div className="section-header">
                <h5>
                  <i className="fas fa-list-ol mr-2"></i>
                  Advertisement Display Order for: {selectedEvent && selectedEvent.eventName}
                </h5>
                <div className="section-actions">
                  {eventAdvertisements.length > 0 && (
                    <Button
                      className="c-btn c-primary"
                      onClick={saveDisplayOrder}
                      disabled={saving}
                    >
                      {saving ? (
                        <React.Fragment>
                          <i className="fas fa-spinner fa-spin mr-2"></i>
                          Saving...
                        </React.Fragment>
                      ) : (
                        <React.Fragment>
                          <i className="fas fa-save mr-2"></i>
                          Save Order
                        </React.Fragment>
                      )}
                    </Button>
                  )}
                </div>
              </div>

              {loading ? (
                <div className="text-center py-4">
                  <i className="fas fa-spinner fa-spin fa-2x"></i>
                  <p>Loading advertisements...</p>
                </div>
              ) : eventAdvertisements.length === 0 ? (
                <div className="no-advertisements">
                  <i className="fas fa-inbox fa-3x text-muted"></i>
                  <h5 className="text-muted mt-3">No advertisements assigned</h5>
                  <p className="text-muted">
                    No advertisements are currently assigned to this event
                  </p>
                </div>
              ) : (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="advertisements">
                    {(provided, snapshot) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className={`advertisements-list ${
                          snapshot.isDraggingOver ? 'dragging-over' : ''
                        }`}
                      >
                        {eventAdvertisements.map((ad, index) => (
                          <Draggable key={ad.id} draggableId={ad.id} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={`advertisement-item ${
                                  snapshot.isDragging ? 'dragging' : ''
                                }`}
                              >
                                <div className="item-order">
                                  <div className="order-number">{index + 1}</div>
                                  <div className="drag-handle">
                                    <i className="fas fa-grip-vertical"></i>
                                  </div>
                                </div>
                                
                                <div className="item-content">
                                  {ad.fileUrl && (
                                    <div className="item-preview">
                                      {ad.mediaType === 'image' ? (
                                        <img 
                                          src={ad.fileUrl} 
                                          alt={ad.title}
                                          className="preview-image"
                                        />
                                      ) : (
                                        <div className="preview-video">
                                          <i className="fas fa-play-circle"></i>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  
                                  <div className="item-details">
                                    <h6 className="item-title">{ad.title}</h6>
                                    <div className="item-meta">
                                      <span className="media-type">
                                        {ad.mediaType.toUpperCase()}
                                      </span>
                                      {ad.mediaType === 'image' && ad.displayDuration && (
                                        <span className="duration">
                                          {ad.displayDuration}s
                                        </span>
                                      )}
                                      <span className={`status ${ad.isActive ? 'active' : 'inactive'}`}>
                                        {ad.isActive ? 'Active' : 'Inactive'}
                                      </span>
                                    </div>
                                    {ad.description && (
                                      <p className="item-description">{ad.description}</p>
                                    )}
                                  </div>
                                </div>

                                <div className="item-actions">
                                  <Button
                                    className="c-btn c-danger btn-sm"
                                    onClick={() => removeAdvertisementFromEvent(ad.eventAdvertisementId)}
                                    title="Remove from Event"
                                  >
                                    <i className="fas fa-times"></i>
                                  </Button>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </div>
          )}
        </div>
      </div>

      <Toaster ref={toaster} />
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    ...state.themeChanger,
  };
};

export default connect(mapStateToProps, null)(EventAdvertisementManager);
